import React, { useState } from "react";
import { Flame, Wallet, Coins, ShieldCheck } from "lucide-react";
import { useBalance, useReadContract, useWriteContract, useWaitForTransactionReceipt } from "wagmi";
import { useWalletUser, ConnectPrompt, useApp } from "@/App";
import { cronosMainnet } from "@/wagmi";
import { requestClaimVoucher, confirmClaim } from "@/api";
import { InvitePanel } from "@/components/InvitePanel";

const ERC20_ABI = [{
  type: "function", name: "balanceOf", stateMutability: "view",
  inputs: [{ name: "account", type: "address" }],
  outputs: [{ name: "", type: "uint256" }],
}];

const CLAIM_ABI = [{
  type: "function", name: "claim", stateMutability: "nonpayable",
  inputs: [
    { name: "amount", type: "uint256" },
    { name: "nonce", type: "uint256" },
    { name: "deadline", type: "uint256" },
    { name: "signature", type: "bytes" },
  ],
  outputs: [],
}];

export default function ProfilePage() {
  const { isConnected, user, address, refresh } = useWalletUser();
  const { config, showToast } = useApp();
  const { data: croBal } = useBalance({ address, chainId: cronosMainnet.id, query: { enabled: !!address } });
  const [preparing, setPreparing] = useState(false);
  const [voucher, setVoucher] = useState(null);
  const [confirmed, setConfirmed] = useState(null);
  const { writeContract, data: claimHash, isPending: claimPending, error: claimError } = useWriteContract();
  const receipt = useWaitForTransactionReceipt({ hash: claimHash });

  const flameContract = config?.flame_contract;
  const claimsEnabled = !!config?.flame_claims_enabled;

  const { data: onchainFlame, refetch: refetchOnchain } = useReadContract({
    address: flameContract && flameContract.startsWith("0x") ? flameContract : undefined,
    abi: ERC20_ABI,
    functionName: "balanceOf",
    args: address ? [address] : undefined,
    chainId: cronosMainnet.id,
    query: { enabled: !!flameContract && !!address, refetchInterval: 15000 },
  });

  React.useEffect(() => {
    if (receipt.isSuccess && claimHash && !confirmed) {
      confirmClaim(address, claimHash)
        .then((r) => { setConfirmed(r); showToast(`🔥 ${r.amount_claimed} FLAME claimed — see your wallet!`); refresh(); refetchOnchain(); })
        .catch((e) => showToast(e?.response?.data?.detail || "Confirmation failed", "error"));
    }
  }, [receipt.isSuccess, claimHash]);  // eslint-disable-line

  if (!isConnected) return <ConnectPrompt />;

  const onchainFlameFmt = onchainFlame !== undefined ? (Number(onchainFlame) / 1e18).toLocaleString(undefined, { maximumFractionDigits: 2 }) : "—";
  const pendingFlame = user?.pending_voucher?.amount || user?.flame || 0;
  const hasPendingVoucher = !!user?.pending_voucher;

  const requestVoucher = async () => {
    if (preparing) return;
    setPreparing(true); setConfirmed(null);
    try {
      const v = await requestClaimVoucher(address);
      setVoucher(v);
      showToast("Voucher issued — now sign the on-chain claim in your wallet", "info");
    } catch (e) {
      showToast(e?.response?.data?.detail || "Voucher failed", "error");
    } finally { setPreparing(false); }
  };

  const submitClaim = () => {
    if (!voucher) return;
    writeContract({
      address: voucher.contract,
      abi: CLAIM_ABI,
      functionName: "claim",
      args: [
        BigInt(voucher.voucher.amount),
        BigInt(voucher.voucher.nonce),
        BigInt(voucher.voucher.deadline),
        voucher.signature,
      ],
      chainId: cronosMainnet.id,
    });
  };

  return (
    <div className="pt-24 pb-20">
      <div className="max-w-5xl mx-auto px-4">
        <h1 className="font-display text-3xl sm:text-4xl font-black">Profile</h1>
        <div className="mt-2 flex gap-2 flex-wrap items-center">
          <div className="chip font-mono text-xs sm:text-sm break-all max-w-full" data-testid="profile-address">{address}</div>
          <a className="chip hover:brightness-125" target="_blank" rel="noreferrer" href={`https://cronoscan.com/address/${address}`}>Cronoscan ↗</a>
          <div className="chip border-red-500/40 bg-red-500/10"><span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /><span className="font-pixel text-[9px] text-red-200">MAINNET — REAL VALUE</span></div>
        </div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card icon={Wallet} color="text-sky-400" label="CRO" value={croBal ? Number(croBal.formatted).toFixed(4) : "—"} tid="profile-balance" />
          <Card icon={Coins} color="text-cyan-400" label="Credits" value={user?.credits ?? 0} tid="profile-credits" />
          <Card icon={Flame} color="text-orange-500" label="FLAME (pending)" value={user?.flame ?? 0} tid="profile-flame" />
          <Card icon={Flame} color="text-amber-400" label="FLAME on-chain" value={onchainFlameFmt} tid="profile-flame-onchain" />
        </div>

        <div className="mt-6 card-tile card-flame">
          <h3 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2"><Flame className="text-orange-500 flame-flicker" /> Claim Rewards</h3>

          {!claimsEnabled ? (
            <div className="mt-3">
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-200" data-testid="claims-pending-audit">
                <ShieldCheck className="inline mr-2" size={16} />
                Claims are <b>pending the Phase 1 audit + mainnet deploy</b>. Once the FLAME & RewardsClaim contracts are live and env-wired, this panel will activate.
              </div>
            </div>
          ) : (
            <>
              <p className="text-slate-400 text-sm mt-1">
                <b>You pay gas.</b> The backend issues an EIP-712 voucher, you sign a single on-chain tx to <code className="text-amber-300 break-all">{config?.rewards_claim_contract}</code>, and the contract atomically pays you 89%, holds 10% in treasury, burns 1%.
              </p>

              {!hasPendingVoucher && !voucher ? (
                <div className="mt-4 flex items-center gap-3 flex-wrap">
                  <button data-testid="profile-request-voucher-button"
                    onClick={requestVoucher}
                    disabled={preparing || !(user?.flame > 0)}
                    className="btn-tactile btn-flame font-display w-full sm:w-auto">
                    {preparing ? "Preparing voucher…" : `Request Voucher (${user?.flame ?? 0} FLAME)`}
                  </button>
                </div>
              ) : (
                <div className="mt-4 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="rounded-xl bg-black/40 border border-orange-500/30 p-4">
                      <div className="font-pixel text-[9px] text-slate-400">YOU'LL RECEIVE (89%)</div>
                      <div className="text-2xl font-display font-black text-orange-400">{Math.floor(pendingFlame * 0.89)} <span className="text-xs">FLAME</span></div>
                    </div>
                    <div className="rounded-xl bg-black/40 border border-sky-500/30 p-4">
                      <div className="font-pixel text-[9px] text-slate-400">TREASURY (10%)</div>
                      <div className="text-2xl font-display font-black text-sky-400">{Math.floor(pendingFlame * 0.10)} <span className="text-xs">FLAME</span></div>
                    </div>
                    <div className="rounded-xl bg-black/40 border border-rose-500/30 p-4">
                      <div className="font-pixel text-[9px] text-slate-400">BURN FEE (1%)</div>
                      <div className="text-2xl font-display font-black text-rose-400">{Math.floor(pendingFlame * 0.01)} <span className="text-xs">FLAME</span></div>
                    </div>
                  </div>

                  {voucher ? (
                    <div className="flex items-center gap-3 flex-wrap">
                      <button data-testid="profile-submit-claim-button"
                        onClick={submitClaim}
                        disabled={claimPending || receipt.isLoading}
                        className="btn-tactile btn-flame font-display w-full sm:w-auto">
                        {claimPending ? "Sign in wallet…" : receipt.isLoading ? "Confirming on-chain…" : "Submit claim tx (you pay gas)"}
                      </button>
                      {claimHash && (
                        <a className="text-xs text-cyan-400 underline break-all" target="_blank" rel="noreferrer"
                           href={`https://cronoscan.com/tx/${claimHash}`}>View tx ↗</a>
                      )}
                      {claimError && <div className="text-rose-400 text-xs">{claimError.shortMessage || claimError.message}</div>}
                    </div>
                  ) : (
                    <div className="text-xs text-slate-400">You have a pending voucher. Reload the page to retrieve it, or wait for it to expire and request a fresh one.</div>
                  )}
                </div>
              )}

              <div className="mt-3 text-xs text-slate-500">
                💡 Add FLAME to your wallet: contract <code className="break-all">{flameContract}</code>, symbol <code>FLAME</code>, decimals <code>18</code>.
              </div>
            </>
          )}
        </div>

        <InvitePanel address={address} />

        <div className="mt-6 card-tile">
          <h3 className="font-display text-2xl font-bold">Stats</h3>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-4">
            <Row label="Total CRO burned" value={Number(user?.cro_burned || 0).toFixed(6)} />
            <Row label="Games played" value={user?.games_played ?? 0} />
            <Row label="Biggest win (FLAME)" value={user?.biggest_win ?? 0} />
            <Row label="Daily streak" value={`${user?.streak ?? 0} 🔥`} />
            <Row label="Total FLAME claimed" value={user?.claims_total ?? 0} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({ icon: Icon, color, label, value, tid }) {
  return (
    <div className="card-tile" data-testid={tid}>
      <div className="flex items-center justify-between">
        <div className="font-pixel text-[9px] text-slate-400">{label}</div>
        <Icon className={color} size={18} />
      </div>
      <div className={`mt-2 font-display text-2xl font-black ${color}`}>{value}</div>
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div>
      <div className="font-pixel text-[9px] text-slate-400">{label}</div>
      <div className="font-mono text-lg">{value}</div>
    </div>
  );
}
